import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  ChangeEvent,
} from 'react';
import { cx } from '../internal/cx';

/**
 * Camada compartilhada de label/hint/erro. Erro nunca é só cor: sempre tem texto
 * (`error`) associado por `aria-describedby` + `aria-invalid`, e o container do erro
 * é `aria-live="polite"` para ser anunciado quando aparece depois de uma submissão.
 * Campo obrigatório nunca é só asterisco: o texto "(obrigatório)" acompanha.
 */
interface FieldShellProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  describedBy?: string;
  children: (describedByIds: string | undefined) => ReactNode;
}

function FieldShell({ id, label, hint, error, required, children }: FieldShellProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="argink-field">
      <label className="argink-field__label" htmlFor={id}>
        {label}
        {required ? <span className="argink-field__required"> (obrigatório)</span> : null}
      </label>
      {hint ? (
        <p className="argink-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {children(describedBy)}
      {error ? (
        <p className="argink-field__error" id={errorId} role="alert" aria-live="polite">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
  containerClassName?: string;
}

/**
 * `forwardRef` porque gerenciar foco é responsabilidade de quem conhece o fluxo,
 * não do campo. O login precisa devolver o foco ao código depois de um reenvio —
 * momento em que o input já está montado e `autoFocus` (que só age na montagem)
 * não faz nada. Sem o ref, a alternativa seria buscar o nó no documento por id,
 * furando o encapsulamento do primitive.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, id, required, containerClassName, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className={containerClassName}>
      <FieldShell id={fieldId} label={label} hint={hint} error={error} required={required}>
        {(describedBy) => (
          <input
            ref={ref}
            id={fieldId}
            className={cx('argink-field__input', 'argink-focusable', Boolean(error) && 'argink-field__input--error')}
            required={required}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
            {...rest}
          />
        )}
      </FieldShell>
    </div>
  );
});

export interface SelectFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'children'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
  options: SelectFieldOption[];
  placeholder?: string;
  containerClassName?: string;
}

export function SelectField({
  label,
  hint,
  error,
  id,
  required,
  options,
  placeholder,
  containerClassName,
  defaultValue,
  ...rest
}: SelectFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  // Sem `value` (controlado) nem `defaultValue` explícito, o placeholder some por
  // padrão do HTML (o navegador seleciona a primeira <option>) — mas deixamos
  // explícito para não depender dessa coincidência de ordem das opções.
  const resolvedDefaultValue = defaultValue ?? (placeholder && rest.value === undefined ? '' : undefined);

  return (
    <div className={containerClassName}>
      <FieldShell id={fieldId} label={label} hint={hint} error={error} required={required}>
        {(describedBy) => (
          <select
            id={fieldId}
            className={cx('argink-field__select', 'argink-focusable', Boolean(error) && 'argink-field__input--error')}
            required={required}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
            defaultValue={resolvedDefaultValue}
            {...rest}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </FieldShell>
    </div>
  );
}

export interface RadioOption {
  value: string;
  label: ReactNode;
  /** Explica a opção em texto — essencial quando as opções (ex.: modalidade de
   * assinatura) não podem se diferenciar só por cor. */
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  legend: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  containerClassName?: string;
}

export function RadioGroup({
  name,
  legend,
  hint,
  error,
  required,
  options,
  value,
  defaultValue,
  onChange,
  containerClassName,
}: RadioGroupProps) {
  const generatedId = useId();
  const groupId = generatedId;
  const hintId = hint ? `${groupId}-hint` : undefined;
  const errorId = error ? `${groupId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange?.(event.target.value);
  }

  return (
    <fieldset
      className="argink-field argink-radio-group"
      aria-describedby={describedBy}
      aria-invalid={Boolean(error) || undefined}
    >
      <legend className="argink-field__label">
        {legend}
        {required ? <span className="argink-field__required"> (obrigatório)</span> : null}
      </legend>
      {hint ? (
        <p className="argink-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      <div className={containerClassName}>
        {options.map((option) => {
          const optionId = `${groupId}-${option.value}`;
          const descId = option.description ? `${optionId}-desc` : undefined;
          return (
            <label key={option.value} className="argink-radio-option" htmlFor={optionId}>
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                disabled={option.disabled}
                checked={value !== undefined ? value === option.value : undefined}
                defaultChecked={value === undefined ? defaultValue === option.value : undefined}
                onChange={handleChange}
                className="argink-radio-option__input argink-focusable"
                aria-describedby={descId}
              />
              <span className="argink-radio-option__body">
                <span className="argink-radio-option__label">{option.label}</span>
                {option.description ? (
                  <span className="argink-radio-option__description" id={descId}>
                    {option.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p className="argink-field__error" id={errorId} role="alert" aria-live="polite">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'type'> {
  label: ReactNode;
  id?: string;
  containerClassName?: string;
}

export function Checkbox({ label, id, containerClassName, ...rest }: CheckboxProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <label className={cx('argink-checkbox', containerClassName)} htmlFor={fieldId}>
      <input
        id={fieldId}
        type="checkbox"
        className="argink-checkbox__input argink-focusable"
        {...rest}
      />
      <span className="argink-checkbox__label">{label}</span>
    </label>
  );
}
